import { liteClient } from "algoliasearch/lite";
import { useMemo } from "react";
import { getAlgoliaIndexName } from "./helpers";
import type { AlgoliaSearchClient } from "./types";

// Kept in its own file on purpose, never importing the full algoliasearch package (that's
// algoliaBrowseClient.ts). Splitting these into two functions in one file used to seem like
// enough, but the bundler tree-shakes per file, not per function, so both SDKs still ended up
// in every bundle. Separate files is what actually keeps the full SDK out of search-only pages.
const SEARCH_API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_API_KEY || "";
const APP_ID = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || "";

// Only the API key matters here (app id is fixed, and the client isn't tied to an index),
// so in practice this cache only ever holds one entry.
const searchClientCache = new Map<string, AlgoliaSearchClient>();

const getOrCreateSearchClient = (apiKey: string): AlgoliaSearchClient => {
    if (!searchClientCache.has(apiKey)) {
        searchClientCache.set(apiKey, liteClient(APP_ID, apiKey));
    }

    return searchClientCache.get(apiKey)!;
};

// No locale param since the client itself doesn't change per locale. Callers get the index
// name separately from getAlgoliaIndexName below and pass it to InstantSearch/queries.
export const algoliaSearch = (): AlgoliaSearchClient => {
    return getOrCreateSearchClient(SEARCH_API_KEY);
};

export const useAlgoliaSearchClient = (
    locale: string,
    mode: "products" | "stores" = "products"
): { searchClient: AlgoliaSearchClient; indexName: string } =>
    useMemo(
        () => ({
            searchClient: algoliaSearch(),
            indexName: getAlgoliaIndexName(locale, mode),
        }),
        [locale, mode]
    );
